import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';

export interface CloneJobStatus {
  job_id: string;
  scenario_id: string | null;
  status: 'pending' | 'running' | 'completed' | 'failed';
  total_articles: number;
  copied_articles: number;
  total_chunks: number;
  copied_chunks: number;
  error?: string | null;
}

const POLL_INTERVAL_MS = 3000;

interface CloneJobWatcherProps {
  jobId: string;
  onSettled: (jobId: string) => void;
}

/**
 * Watches one queued copy until it lands, then announces it.
 *
 * Rendered only while a copy is in flight, so the switcher does not carry a
 * polling query the rest of the time. Polling rather than a socket: the user
 * stays on the source agent's page, and the copy's own room does not exist for
 * them until it is revealed.
 */
export const CloneJobWatcher = ({ jobId, onSettled }: CloneJobWatcherProps) => {
  const { t } = useTranslation('sidebar_menu');
  const queryClient = useQueryClient();
  // The effect below can re-run with the same terminal status before the parent
  // unmounts this; the outcome must be announced exactly once.
  const settled = useRef(false);

  const { data, error, isError } = useQuery<CloneJobStatus, AxiosError>({
    queryKey: [QueryKey.aiAssistants.cloneStatus, jobId],
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'completed' || status === 'failed' ? false : POLL_INTERVAL_MS;
    },
  });

  useEffect(() => {
    if (settled.current) return;
    if (isError && [403, 404, 422].includes(error?.response?.status ?? 0)) {
      // An inaccessible or malformed stored job has nothing to announce.
      settled.current = true;
      onSettled(jobId);
      return;
    }
    if (!data) return;
    if (data.status === 'completed') {
      settled.current = true;
      onSettled(jobId);
      // The copy is a new agent the switcher has not heard of.
      void queryClient.invalidateQueries({ queryKey: [QueryKey.aiAssistants.get] });
      if (!data.scenario_id) {
        toast.error(t('clone.error.failed'));
        return;
      }
      toast.success(t('clone.queued.ready'), {
        action: {
          label: t('clone.queued.open'),
          onClick: () => window.location.replace(`/i/${data.scenario_id}`),
        },
        duration: Infinity,
      });
    }
    if (data.status === 'failed') {
      settled.current = true;
      onSettled(jobId);
      toast.error(t('clone.error.failed'));
    }
  }, [data, error, isError, jobId, onSettled, queryClient, t]);

  return null;
};
